import React, { useEffect, useState } from 'react';
import { Button, CircularProgress, withStyles } from '@material-ui/core';
import CustomMultiSelect from '../../../Dispatch/RouteExecution/CustomMultiSelect';
import { getDataWithQueryString, postData } from '../../../../../api/componentAPIs';
import {
  filterListBasedOnSelectedList,
  getPayloadForUsageReport,
  getUniqueOptionsByKey,
  transformUsageData
} from '../utils';
import { withSnackbar } from 'notistack';
import { compose } from 'recompose';
import DateRangePicker from '../../../../common/MUI/DateRangePicker';
import MESSAGES from '../../../../../appMessages';
import { getPastDate } from '../../../../common/utils/dateTimeUtils';
import constants from '../constants';
import { withUserProfile } from '../../../../../userProfile/userProfileContext';

const styles = (theme) => ({
  container: {
    color: theme.TextColor,
    paddingBottom: 10
  },
  filterContainer: {
    marginTop: 10,
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  },
  multiSelect: {
    width: 240,
    margin: '5px 0',
    padding: 10
  },
  filterInput: {
    maxWidth: 240,
    margin: '5px 0'
  },
  buttonRoot: {
    minWidth: 140,
    '&$disabledButton': {
      backgroundColor: theme.disabledColor,
      color: theme.TextColor,
      opacity: 0.8
    }
  },
  circularProgress: {
    color: theme.white
  }
});

const defaultDateRange = {
  startDate: getPastDate(constants.DEFAULT_SELECTED_DATE_RANGE - 1),
  endDate: new Date()
};

const SearchBy = ({ classes, enqueueSnackbar, setUsageData, userData }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [allTerminalsData, setAllTerminalsData] = useState([]);
  const [dropDownOptions, setDropDownOptions] = useState({
    terminals: [],
    regions: [],
    divisions: []
  });

  const terminalDataFieldNames = {
    divisionName: 'divisionName',
    regionName: 'regionName',
    terminalCode: 'terminalCode'
  };

  const [filters, setFilters] = useState({
    terminals: [],
    regions: [],
    divisions: [],
    dateRange: defaultDateRange
  });

  useEffect(() => {
    handleInitialization();
  }, []);

  const handleInitialization = async () => {
    const initializedFilters = await populateAndInitializeDropDownOptions();
    await getUsageDetails(initializedFilters);
  };

  // ================ API Calls ================

  const getTerminalsList = async () => {
    try {
      const response = await getDataWithQueryString('GetAllTerminals');
      if (response.status === 200) {
        return response.data;
      }
      handleError(response, MESSAGES.USAGE_REPORT.FETCH_PUBLISHED_TERMINALS_FAILURE);
      return [];
    } catch (error) {
      handleError(error, MESSAGES.USAGE_REPORT.FETCH_PUBLISHED_TERMINALS_FAILURE);
      return [];
    }
  };

  const getUsageDetails = async (initialFilters) => {
    try {
      setIsLoading(true);
      const currentFilters = initialFilters || filters;
      if (!isFiltersValid(currentFilters)) {
        return;
      }
      const payload = getPayloadForUsageReport(currentFilters);
      const response = await postData('getDynamicRoutingUsageReport', payload);
      if (response.status === 200) {
        const transformedData = transformUsageData(response.data);
        setUsageData(transformedData);
      } else {
        handleError(response, MESSAGES.USAGE_REPORT.FETCH_USAGE_DETAILS_FAILURE);
      }
    } catch (error) {
      handleError(error, MESSAGES.USAGE_REPORT.FETCH_USAGE_DETAILS_FAILURE);
    } finally {
      setIsLoading(false);
    }
  };

  // ================ Error Handling & Validation ================

  const isFiltersValid = (filters) => {
    if (!filters.terminals?.length) {
      enqueueSnackbar(MESSAGES.USAGE_REPORT.SELECT_TERMINAL, { variant: 'error' });
      return false;
    }
    if (!filters.dateRange || !filters.dateRange.startDate || !filters.dateRange.endDate) {
      enqueueSnackbar(MESSAGES.USAGE_REPORT.SELECT_DATE_RANGE, { variant: 'error' });
      return false;
    }
    return true;
  };

  const handleError = (error, message) => {
    const errorMessage = error.response?.data?.message || message;
    enqueueSnackbar(errorMessage, { variant: 'error' });
  };

  // ================ Dropdown Options ================

  const populateAndInitializeDropDownOptions = async () => {
    const terminalsList = await getTerminalsList();
    setAllTerminalsData(terminalsList || []);
    const divisionOptions = getUniqueOptionsByKey(
      terminalsList,
      terminalDataFieldNames.divisionName
    );
    const regionOptions = getUniqueOptionsByKey(terminalsList, terminalDataFieldNames.regionName);
    const terminalOptions = getUniqueOptionsByKey(
      terminalsList,
      terminalDataFieldNames.terminalCode
    );

    const options = {
      terminals: terminalOptions,
      regions: regionOptions,
      divisions: divisionOptions
    };
    setDropDownOptions(options);
    const initializedFilters = initializeFilters(terminalsList);
    return initializedFilters;
  };

  const initializeFilters = (terminalsList) => {
    const defaultFilters = getUserTerminalFilters(terminalsList);
    const initialFilters = {
      ...defaultFilters,
      dateRange: filters?.dateRange || defaultDateRange
    };
    setFilters(initialFilters);
    return initialFilters;
  };

  const getUserTerminalFilters = (terminalsList) => {
    const terminalCode = userData?.userProfile?.terminal?.code;

    const terminalData = terminalsList?.find((terminal) => terminal.terminalCode === terminalCode);
    if (!terminalData) return { terminals: [], divisions: [], regions: [] };

    const getOptionsList = (value) => (value ? [{ value, label: value }] : []);
    return {
      terminals: getOptionsList(terminalCode),
      divisions: getOptionsList(terminalData.divisionName),
      regions: getOptionsList(terminalData.regionName)
    };
  };

  // ================ OnChange Handlers ================

  const handleDivisionChange = (divisions) => {
    const filtered = filterListBasedOnSelectedList(
      allTerminalsData,
      divisions,
      terminalDataFieldNames.divisionName
    );

    const regionOptions = getUniqueOptionsByKey(filtered, terminalDataFieldNames.regionName);
    const terminalOptions = getUniqueOptionsByKey(filtered, terminalDataFieldNames.terminalCode);

    const [selectedRegions, selectedTerminals] = divisions?.length
      ? [regionOptions, terminalOptions]
      : [[], []];

    setDropDownOptions({ ...dropDownOptions, regions: regionOptions, terminals: terminalOptions });
    setFilters({ ...filters, divisions, regions: selectedRegions, terminals: selectedTerminals });
  };

  const handleRegionChange = (regions) => {
    const divisionFiltered = filterListBasedOnSelectedList(
      allTerminalsData,
      filters.divisions,
      terminalDataFieldNames.divisionName
    );
    const regionFiltered = filterListBasedOnSelectedList(
      divisionFiltered,
      regions,
      terminalDataFieldNames.regionName
    );

    const terminalOptions = getUniqueOptionsByKey(
      regionFiltered,
      terminalDataFieldNames.terminalCode
    );

    const selectedTerminals = regions.length ? terminalOptions : [];

    setDropDownOptions({ ...dropDownOptions, terminals: terminalOptions });
    setFilters({ ...filters, regions, terminals: selectedTerminals });
  };

  const handleTerminalChange = (terminals) => {
    setFilters({ ...filters, terminals });
  };

  const handleDateRangeChange = (value) => {
    setFilters({ ...filters, dateRange: value });
  };

  return (
    <div className={classes.container}>
      <div>SEARCH BY</div>
      <div className={classes.filterContainer}>
        <CustomMultiSelect
          label={'Divisions'}
          Options={dropDownOptions.divisions}
          handleChange={handleDivisionChange}
          optionSelected={filters.divisions}
          inputClasses={{ wrapper: classes.filterInput }}
          isMenuLeftAligned={true}
        />
        <CustomMultiSelect
          label={'Regions'}
          Options={dropDownOptions.regions}
          handleChange={handleRegionChange}
          optionSelected={filters.regions}
          inputClasses={{ wrapper: classes.filterInput }}
          isMenuLeftAligned={true}
        />
        <CustomMultiSelect
          label={'Terminals'}
          Options={dropDownOptions.terminals}
          handleChange={handleTerminalChange}
          optionSelected={filters.terminals}
          inputClasses={{ wrapper: classes.filterInput }}
        />
        <DateRangePicker
          isFutureDateAllowed={true}
          maxRange={constants.MAX_DATE_RANGE_LIMIT}
          onChange={handleDateRangeChange}
          value={filters.dateRange}
          staticRanges={constants.STATIC_DATE_RANGES}
          classes={{
            wrapper: classes.filterInput
          }}
        />
        <Button
          variant="contained"
          size="large"
          color="primary"
          classes={{ root: classes.buttonRoot }}
          onClick={() => getUsageDetails()} //To avoid passing the event as a parameter to the function
          disabled={isLoading}
          data-automationid="search">
          {isLoading ? (
            <CircularProgress size={24} classes={{ root: classes.circularProgress }} />
          ) : (
            'Search'
          )}
        </Button>
      </div>
    </div>
  );
};

export default compose(withSnackbar, withUserProfile, withStyles(styles))(SearchBy);
